import { useCallback, useState, useEffectEvent, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Page } from "@boundary/layouts/pageLayout";
import { Button } from "@boundary/components/button";
import { useDebugEmulations } from "@boundary/components/debugWidget";
import { useOrderFormState } from "@boundary/_hooks/useOrderFormState";
import { useCreditCard } from "@boundary/_hooks/useBilling";
import { SelectCard } from "@boundary/widgets/selectCard";
import { CardInfo } from "@domain/dtos/models/card.models";
import { useOrders } from "@boundary/_hooks/useOrders";
import { OrderService } from "@domain/services/order.service";
import { useAuth } from "@boundary/_hooks/useAuth";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import styles from "./payment.module.scss";

// Эмуляции для страницы Payment
const PAYMENT_DEBUG_OPTIONS = [
  { key: "payment_noCards", label: "Нет привязанных карт" },
] as const;

export const PaymentPage = observer(function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const machineIdParam = searchParams.get("machineId");
  const machineId = machineIdParam ? Number(machineIdParam) : 0;
  
  // Debug: регистрируем эмуляции для этой страницы
  const debug = useDebugEmulations(PAYMENT_DEBUG_OPTIONS);
  
  const vm = useOrderFormState(machineId);
  const { savedCards, setCardDefault } = useCreditCard();
  const { activeOrders } = useOrders();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [waitingForOrderNumber, setWaitingForOrderNumber] = useState(false);
  const orderNumberTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousActiveOrdersRef = useRef<Set<string>>(new Set());
  
  // Debug: эмулируем пустой список карт если включено
  const effectiveCards = debug.payment_noCards ? [] : savedCards;
  const hasPaymentMethods = effectiveCards.length > 0;

  const handleCardSelected = useCallback((card: CardInfo) => {
    void setCardDefault(card.id);
  }, [setCardDefault]);

  // Инициализируем множество известных заказов при монтировании
  useEffect(() => {
    const currentOrderNumbers = new Set(activeOrders.map(o => o.order_number));
    previousActiveOrdersRef.current = currentOrderNumbers;
  }, []); // Только при монтировании

  // Следим за появлением нового заказа в activeOrders через WebSocket
  // После получения {status: "success"} заказ уже создан, просто ждем его через WebSocket
  useEffect(() => {
    if (waitingForOrderNumber) {
      const currentOrderNumbers = new Set(activeOrders.map(o => o.order_number));
      const newOrders = activeOrders.filter(
        order => !previousActiveOrdersRef.current.has(order.order_number)
      );
      
      if (newOrders.length > 0) {
        // Фильтруем новые заказы по machine_id (если есть несколько активных заказов)
        const relevantNewOrders = newOrders.filter(order => {
          const orderMachineId = (order as any).machine_id;
          // Если есть machine_id, проверяем что он совпадает с текущим
          // Если нет machine_id, считаем что это наш заказ
          return !orderMachineId || orderMachineId === machineId;
        });
        
        if (relevantNewOrders.length > 0) {
          // Если несколько новых заказов, берем самый свежий (по created_at или первый в массиве)
          const newOrder = relevantNewOrders.sort((a, b) => {
            if (a.created_at && b.created_at) {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
            return 0; // Если нет created_at, порядок не важен
          })[0];
          
          console.log("[PaymentPage] New order appeared in activeOrders via WebSocket:", newOrder.order_number);
          
          if (orderNumberTimeoutRef.current) {
            clearTimeout(orderNumberTimeoutRef.current);
            orderNumberTimeoutRef.current = null;
          }
          
          setWaitingForOrderNumber(false);
          setLoading(false);
          
          // Обновляем множество известных заказов
          previousActiveOrdersRef.current = currentOrderNumbers;
          
          // Переходим на страницу успеха с order_number
          prefetchAndNavigate(navigate, `/payment/status?status=success&machineId=${machineId}&orderNumber=${newOrder.order_number}`);
        } else {
          // Новые заказы есть, но не для текущего аппарата - обновляем множество
          previousActiveOrdersRef.current = currentOrderNumbers;
        }
      } else {
        // Новых заказов нет - обновляем множество известных заказов
        previousActiveOrdersRef.current = currentOrderNumbers;
      }
    } else {
      // Обновляем множество известных заказов когда не ждем
      const currentOrderNumbers = new Set(activeOrders.map(o => o.order_number));
      previousActiveOrdersRef.current = currentOrderNumbers;
    }
  }, [activeOrders, waitingForOrderNumber, navigate, machineId]);

  const handleProceedPayment = useEffectEvent(async () => {
    setLoading(true);
    setRetryCount(0);
    setWaitingForOrderNumber(false);
    
    // Запоминаем текущие активные заказы перед началом оплаты
    const currentOrderNumbers = new Set(activeOrders.map(o => o.order_number));
    previousActiveOrdersRef.current = currentOrderNumbers;
    
    // Очищаем предыдущий timeout если есть
    if (orderNumberTimeoutRef.current) {
      clearTimeout(orderNumberTimeoutRef.current);
      orderNumberTimeoutRef.current = null;
    }
    
    const attemptPayment = async (attempt: number): Promise<void> => {
      try {
        await vm.proceedPayment();
        
        // Заказ создан успешно, переходим на страницу статуса
        console.log("[PaymentPage] Order created successfully, redirecting to status page");
        setLoading(false);
        prefetchAndNavigate(navigate, `/payment/status?status=success&machineId=${machineId}`);
        
      } catch (error) {
        console.error(`[PaymentPage] Payment failed (attempt ${attempt}):`, error);
        
        // Очищаем timeout при ошибке
        if (orderNumberTimeoutRef.current) {
          clearTimeout(orderNumberTimeoutRef.current);
          orderNumberTimeoutRef.current = null;
        }
        
        // Retry логика для таймаутов и серверных ошибок
        if (attempt < 2 && error instanceof Error && 
            (error.message.includes("timeout") || error.message.includes("500"))) {
          console.log(`[PaymentPage] Retrying payment (attempt ${attempt + 1}/2)`);
          setRetryCount(attempt);
          await new Promise(resolve => setTimeout(resolve, 2000)); // Ждем 2 секунды
          return attemptPayment(attempt + 1);
        }
        
        setLoading(false);
        setWaitingForOrderNumber(false);
        
        // Показываем более информативные сообщения об ошибках
        if (error instanceof Error && error.message.includes("timeout")) {
          prefetchAndNavigate(navigate, `/payment/status?status=timeout&machineId=${machineId}`);
        } else if (error instanceof Error && error.message.includes("Не удалось сформировать позиции для чека")) {
          prefetchAndNavigate(navigate, `/payment/status?status=receipt_error&machineId=${machineId}`);
        } else {
          prefetchAndNavigate(navigate, `/payment/status?status=fail&machineId=${machineId}`);
        }
      }
    };
    
    await attemptPayment(1);
  });

  const handleChangePayment = useCallback(() => {
    prefetchAndNavigate(navigate, `/payment/settings?machineId=${machineId}`);
  }, [navigate, machineId]);

  return (
    <Page>
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Header back />
        <Page.DisplayZone.Body className={styles.content}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>Необходимо выбрать способ оплаты</div>
            {!hasPaymentMethods && (
              <p className={styles.headerDescription}>
                На данный момент нет привязанного способа оплаты
              </p>
            )}
          </div>
          <div className={styles.cardsSection}>
            <SelectCard onCardSelected={handleCardSelected} hideNewCard={hasPaymentMethods} cards={effectiveCards} />
            {hasPaymentMethods && (
              <button className={styles.changePaymentLink} onClick={handleChangePayment}>
                Поменять способ оплаты
              </button>
            )}
          </div>
        </Page.DisplayZone.Body>
        {effectiveCards.length > 0 && (
          <Page.DisplayZone.Sticky className={styles.buttonPad}>
            {loading && (
              <div className={styles.loadingMessage}>
                {retryCount > 0 
                  ? `Повторная попытка (${retryCount + 1}/2)...` 
                  : waitingForOrderNumber
                  ? "Ожидаем подтверждение создания заказа..."
                  : "Создаём заказ… Это может занять некоторое время"
                }
              </div>
            )}
            <Button 
              onClick={handleProceedPayment} 
              disabled={loading || vm.services.length === 0}
            >
              {loading ? "Создаём заказ…" : "Оплатить"}
            </Button>
          </Page.DisplayZone.Sticky>
        )}
      </Page.DisplayZone>
    </Page>
  );
});
